import type { BucketDefinition, Field, Placement, RoleChange } from "./definitions";

/** The vertical extent of a zone or a field on screen; a `DOMRect` fits */
export interface Box {
    top: number;
    bottom: number;
}

/** A drop target with its box on screen */
export interface DropZone {
    role: Placement;
    box: Box;
}

/**
 * The first zone whose box spans the pointer's y; the x plays no part, since a dragged field only moves vertically.
 * Misses are retried with every box stretched by `tolerance` pixels up and down, so a release just under a bucket's
 * last field still lands; null when that fails too.
 */
export function zoneFromY<T extends { box: Box }>(y: number, zones: T[], tolerance: number = 0): T | null {
    const within = (margin: number): T | undefined => zones.find(({ box }) => y >= box.top - margin && y <= box.bottom + margin);
    return within(0) ?? within(tolerance) ?? null;
}

/**
 * What dropping a field somewhere changes: the dropped field takes the target placement. When the target bucket is already at its
 * `maxFields`, its bottom movable field is swapped out and takes the dropped field's old placement. Empty array for a fixed
 * field, a drop on the own placement, on a bucket that does not accept the field's kind, or on a full bucket with no movable
 * field to swap out or whose swapped-out field the old bucket does not accept.
 */
export function resolveFieldDrop(droppedField: Field, target: Placement, buckets: BucketDefinition[], fields: Field[]): RoleChange[] {
    if (!droppedField.movable || target === droppedField.role) {
        return [];
    }
    if (target === null) {
        return [{ fieldId: droppedField.id, role: null }];
    }
    const targetBucket = buckets.find((bucket) => bucket.role === target);
    if (!targetBucket?.accepts.includes(droppedField.kind)) {
        return [];
    }
    const changes: RoleChange[] = [{ fieldId: droppedField.id, role: target }];
    const existingFieldsInBucket = fields.filter((field) => field.role === target && field.id !== droppedField.id);
    if (existingFieldsInBucket.length >= targetBucket.maxFields) {
        // the swap: the bottom movable field takes the dropped field's old role; a bucket full of fixed fields takes no drop
        const swappedOut = existingFieldsInBucket.filter((field) => field.movable).at(-1);
        if (!swappedOut || !accepts(droppedField.role, swappedOut, buckets)) {
            return [];
        }
        changes.push({ fieldId: swappedOut.id, role: droppedField.role });
    }
    return changes;
}

/** Whether a placement takes a field of this kind; the list (`null`) takes every kind */
function accepts(placement: Placement, field: Field, buckets: BucketDefinition[]): boolean {
    if (placement === null) {
        return true;
    }
    const bucket = buckets.find((candidate) => candidate.role === placement);
    return bucket?.accepts.includes(field.kind) ?? false;
}

/**
 * The bucket a field goes to when ticked in the Fields list: the first bucket that accepts its kind and has room, or null when
 * every such bucket is full
 */
export function defaultBucketFor(field: Field, buckets: BucketDefinition[], fields: Field[]): Placement {
    const bucket = buckets.find((candidate) => {
        const count = fields.filter((other) => other.role === candidate.role && other.id !== field.id).length;
        return candidate.accepts.includes(field.kind) && count < candidate.maxFields;
    });
    return bucket?.role ?? null;
}

/** The fields with the changes applied, in the same order; a change for an unknown id is ignored */
export function applyRoleChanges(fields: Field[], changes: RoleChange[]): Field[] {
    return fields.map((field) => {
        const change = changes.find((candidate) => candidate.fieldId === field.id);
        return change ? { ...field, role: change.role } : field;
    });
}

/** The index of a dropped field among the bucket's fields, from the pointer's y and the field boxes */
export function insertionIndex(pointerY: number, fieldBoxes: Box[]): number {
    const index = fieldBoxes.findIndex((box) => pointerY < (box.top + box.bottom) / 2);
    return index === -1 ? fieldBoxes.length : index;
}
